/**
 * StyleCache.ts - Performance caching for Stylr
 * 
 * CHANGE HISTORY:
 * - 251102_1646 danielbeer: Created for Phase 3.4 performance optimization
 * 
 * Provides:
 * - CSS generation caching
 * - Resolved style caching
 * - Cache invalidation strategies
 * - Memory-efficient LRU cache
 */

/**
 * LRU Cache implementation for style data
 */
export class LRUCache<K, V> {
  private cache: Map<K, V>;
  private maxSize: number;
  private accessOrder: K[];

  constructor(maxSize = 100) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.accessOrder = [];
  }

  get(key: K): V | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // Move to end (most recently used)
      this.updateAccessOrder(key);
    }
    return value;
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      // Update existing
      this.cache.set(key, value);
      this.updateAccessOrder(key);
    } else {
      // Add new
      if (this.cache.size >= this.maxSize) {
        // Evict least recently used
        const lru = this.accessOrder.shift();
        if (lru !== undefined) {
          this.cache.delete(lru);
        }
      }
      this.cache.set(key, value);
      this.accessOrder.push(key);
    }
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  delete(key: K): boolean {
    const deleted = this.cache.delete(key);
    if (deleted) {
      const index = this.accessOrder.indexOf(key);
      if (index > -1) {
        this.accessOrder.splice(index, 1);
      }
    }
    return deleted;
  }

  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
  }

  size(): number {
    return this.cache.size;
  }

  private updateAccessOrder(key: K): void {
    const index = this.accessOrder.indexOf(key);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }
    this.accessOrder.push(key);
  }

  // Get cache statistics
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      utilizationPercent: (this.cache.size / this.maxSize * 100).toFixed(1)
    };
  }
}

/**
 * Style-specific cache manager
 */
export class StyleCache {
  private cssCache: LRUCache<string, string>;
  private resolvedStyleCache: LRUCache<string, any>;
  private hits: number = 0;
  private misses: number = 0;

  constructor(maxSize = 100) {
    this.cssCache = new LRUCache(maxSize);
    this.resolvedStyleCache = new LRUCache(maxSize);
  }

  /**
   * Get cached CSS
   */
  getCSS(cacheKey: string): string | undefined {
    const cached = this.cssCache.get(cacheKey);
    if (cached) {
      this.hits++;
      return cached;
    }
    this.misses++;
    return undefined;
  }

  /**
   * Cache CSS
   */
  setCSS(cacheKey: string, css: string): void {
    this.cssCache.set(cacheKey, css);
  }

  /**
   * Get cached resolved style
   */
  getResolvedStyle(styleId: string): any | undefined {
    const cached = this.resolvedStyleCache.get(styleId);
    if (cached) {
      this.hits++;
      return cached;
    }
    this.misses++;
    return undefined;
  }

  /**
   * Cache resolved style
   */
  setResolvedStyle(styleId: string, style: any): void {
    this.resolvedStyleCache.set(styleId, style);
  }

  /**
   * Invalidate cache for a specific style
   */
  invalidateStyle(styleId: string): void {
    this.resolvedStyleCache.delete(styleId);
    
    // Invalidate all CSS cache entries that might use this style
    // (This is conservative - could be optimized with better key tracking)
    this.cssCache.clear();
  }

  /**
   * Invalidate all caches
   */
  invalidateAll(): void {
    this.cssCache.clear();
    this.resolvedStyleCache.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const total = this.hits + this.misses;
    const hitRate = total > 0 ? (this.hits / total * 100).toFixed(1) : '0.0';
    
    return {
      hits: this.hits,
      misses: this.misses,
      hitRate: `${hitRate}%`,
      css: this.cssCache.getStats(),
      resolvedStyles: this.resolvedStyleCache.getStats()
    };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.hits = 0;
    this.misses = 0;
  }
}

/**
 * Create cache key for CSS generation
 */
export function createCSSCacheKey(styleId: string, selector: string): string {
  return `${styleId}:${selector}`;
}

/**
 * Singleton cache instance
 */
let cacheInstance: StyleCache | null = null;

export function getStyleCache(): StyleCache {
  if (!cacheInstance) {
    cacheInstance = new StyleCache();
  }
  return cacheInstance;
}

export function resetStyleCache(): void {
  cacheInstance = null;
}
